import { memo, useEffect, useState } from 'react';

import { Tooltip } from 'components/app-components/ui/tooltip';
import CopyIcon from 'icons/CopyIcon';
import Tick from 'icons/Tick';

interface IProps {
  text: string;
  onlyIconMode?: boolean;
  showCopyIcon?: boolean;
  containerClassName?: string;
  showBorder?: boolean;
  textClassName?: string;
  label?: string;
  showTextAtTheBeginning?: boolean;
  showIsCopiedText?: boolean;
  trim?: boolean;
  iconStrokeColor?: string;
}

const CopyableText: React.FC<IProps> = ({
  text,
  onlyIconMode,
  containerClassName,
  textClassName,
  showCopyIcon = true,
  showBorder = true,
  label,
  showTextAtTheBeginning,
  showIsCopiedText = false,
  trim = false,
  iconStrokeColor,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isDemoEnv, setIsDemoEnv] = useState(false);

  const handleCopyClick = (e) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(text)
      .then(() => setIsCopied(true))
      .catch((err) => console.error('Could not copy text: ', err));
  };

  useEffect(() => {
    if (isCopied) {
      const timeoutId = setTimeout(() => {
        setIsCopied(false);
      }, 1200);

      return () => clearTimeout(timeoutId);
    }
  }, [isCopied]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname === 'demo.app.getthera.com') {
      setIsDemoEnv(true);
    }
  }, []);

  if (isDemoEnv) {
    return null;
  }

  const getIconColorClass = () => {
    if (onlyIconMode) return '';
    if (iconStrokeColor === 'white') return 'text-white';
    if (iconStrokeColor === 'black') return 'text-black';
    if (iconStrokeColor === 'gray') return 'text-gray-500';
    return 'text-therablue';
  };

  return (
    <div
      onClick={handleCopyClick}
      className={`flex items-center${containerClassName ? ` ${containerClassName}` : ''}`}
      role="button"
    >
      {showTextAtTheBeginning && <span className={`mr-2 text-xs ${textClassName ? textClassName : ''}`}>{text}</span>}
      <div className="flex items-center">
        <Tooltip content={isCopied ? 'Copied!' : 'Click to Copy'} className="w-max !text-xs font-medium shadow-md">
          <span
            className={`${
              showBorder ? 'rounded-md border border-dotted border-therablue-light p-1' : ''
            } flex cursor-pointer items-center text-xs ${textClassName ? textClassName : ''}`}
          >
            {label && `${label} : `}
            {!onlyIconMode && !showTextAtTheBeginning ? (
              <span className={trim ? 'max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap' : ''}>
                {text}
              </span>
            ) : null}
            {showCopyIcon && !isCopied && <CopyIcon className={`ml-1 h-3 w-3 ${getIconColorClass()}`} />}
          </span>
        </Tooltip>
        {isCopied ? (
          showIsCopiedText ? (
            <span className={`ml-2 text-xs ${textClassName ? textClassName : ''}`}>Copied!</span>
          ) : (
            <Tick className={`h-3 w-3 ${getIconColorClass()}`} />
          )
        ) : null}
      </div>
    </div>
  );
};

const MemoizedCopyableText = memo(CopyableText);

export default MemoizedCopyableText;
